import {
  cargoSubtype,
  CompanyEntity,
  FlowAllocationType,
  FreightTypeEntity,
  getObjectId,
  isBefore,
  PipelineEntity,
  ServiceTypeEntity,
  TimeGroup,
  toFlowAllocationType,
  toTimeGroup,
  TransportCategory,
  transportCategory
} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Group, Modal, ModalProps} from "@mantine/core";
import {useCallback} from "react";
import {DateInput} from "../../date/DateInput/DateInput";
import {FreightTypeSelect} from "../../select/entity/FreightTypeSelect/FreightTypeSelect";
import {TimeGroupSelect} from "../../select/entity/TimeGroupSelect/TimeGroupSelect";
import {FlowAllocationTypeSelect} from "../../select/entity/FlowAllocationTypeSelect/FlowAllocationTypeSelect";
import {PipelineSelect} from "../../select/entity/PipelineSelect/PipelineSelect";
import {DefaultTFuncReturn} from "i18next";
import {ServiceTypeSelect} from "../../select/entity/ServiceTypeSelect/ServiceTypeSelect";
import { CompanySelect } from "../../select/entity/CompanySelect/CompanySelect";

interface PipelineFlowTransportSearchFormProps {
  options?: PipelineFlowTransportSearchFormOptions;
  defaultValues?: Partial<PipelineFlowTransportSearchFormValues>;
  onSubmit?: (values: PipelineFlowTransportSearchFormValues) => void;
  submitting?: boolean;
}

export interface PipelineFlowTransportSearchFormOptions {
  regionId?: string
  category?: TransportCategory
}

export interface PipelineFlowTransportSearchFormValues {
  fromDate: Date
  toDate?: Date | undefined
  freightType?: FreightTypeEntity | string;
  serviceType?: ServiceTypeEntity | string
  pipeline?: PipelineEntity | string
  pipelineCompany?: CompanyEntity | string
  timeGroup?: TimeGroup
  allocationType?: FlowAllocationType
}

const getDefaultValues = (defaultValues: Partial<PipelineFlowTransportSearchFormValues> | undefined) => {
  return {
    fromDate: defaultValues?.fromDate,
    toDate: defaultValues?.toDate,
    freightType: getObjectId(defaultValues?.freightType),
    serviceType: getObjectId(defaultValues?.serviceType),
    pipeline: getObjectId(defaultValues?.pipeline),
    pipelineCompany: getObjectId(defaultValues?.pipelineCompany),
    timeGroup: defaultValues?.timeGroup?.code,
    allocationType: defaultValues?.allocationType?.code,
  }
}

export const PipelineFlowTransportSearchForm = (props: PipelineFlowTransportSearchFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {
      toDate: (value, values) => {
        let error: string | undefined | DefaultTFuncReturn = undefined
        if (value && values.fromDate && isBefore(value, values.fromDate)) {
          error = t<string>("common.form.validation.todatebeforefrom", "The 'to' date can't be before the 'from' date")
        }
        return error
      }
    }
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        fromDate: values.fromDate!,
        toDate: values.toDate,
        freightType: values.freightType,
        serviceType: values.serviceType,
        pipeline: values.pipeline,
        pipelineCompany: values.pipelineCompany,
        timeGroup: values.timeGroup ? toTimeGroup(values.timeGroup) : undefined,
        allocationType: values.allocationType ? toFlowAllocationType(values.allocationType) : undefined
      })
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Group align="flex-end">
        <Grid>
          <Grid.Col span={6}>
            <DateInput
              valueFormat="MM/DD/YYYY HH:mm"
              label={t("common.entity.pipelineflowtransport.search.form.field.fromdate", "From Date")}
              placeholder={t<string>("common.entity.pipelineflowtransport.search.form.field.fromdate", "From Date")}
              clearable
              withAsterisk
              popoverProps={{
                withinPortal: true
              }}
              styles={{wrapper: {width: "200px"}}}
              required
              {...form.getInputProps("fromDate")} />
          </Grid.Col>
          <Grid.Col span={6}>
            <DateInput
              miw={100}
              valueFormat="MM/DD/YYYY HH:mm"
              label={t("common.entity.pipelineflowtransport.search.form.field.todate", "To Date")}
              placeholder={t<string>("common.entity.pipelineflowtransport.search.form.field.todate", "To Date")}
              clearable
              popoverProps={{
                withinPortal: true
              }}
              styles={{wrapper: {width: "200px"}}}
              {...form.getInputProps("toDate")} />
          </Grid.Col>
          <Grid.Col span={12}>
            <TimeGroupSelect
              label={t("common.entity.pipelineflowtransport.search.form.field.timegroup", "Time Group")}
              placeholder={t<string>("common.entity.pipelineflowtransport.search.form.field.timegroup", "Time Group")}
              clearable
              withinPortal={true}
              {...form.getInputProps("timeGroup")} />
          </Grid.Col>
        </Grid>
      </Group>
      <Divider my="sm" variant="dotted"/>
      <FlowAllocationTypeSelect
        label={t("common.entity.pipelineflowtransport.search.form.field.allocation", "Allocation")}
        placeholder={t<string>("common.entity.pipelineflowtransport.search.form.field.allocation", "Allocation")}
        clearable
        withinPortal={true}
        {...form.getInputProps("allocationType")} />
      <Divider my="sm" variant="dotted"/>
      <PipelineSelect
        label={t("common.entity.pipelineflowtransport.search.form.field.pipeline", "Pipeline")}
        placeholder={t<string>("common.entity.pipelineflowtransport.search.form.field.object.pipeline", "Pipeline")}
        selector={{
          regionId: options?.regionId,
        }}
        clearable
        withinPortal={true}
        {...form.getInputProps("pipeline")} />
      <Divider my="sm" variant="dotted"/>
      <CompanySelect
        label={t("common.entity.pipelineflowtransport.search.form.field.pipelinecompany", "Pipeline Company")}
        placeholder={t<string>("common.entity.pipelineflowtransport.search.form.field.object.pipelinecompany", "Pipeline Company")}
        selector={{
          regionId: options?.regionId,
        }}
        clearable
        withinPortal={true}
        {...form.getInputProps("pipelineCompany")} />
      <Divider my="sm" variant="dotted"/>
      <FreightTypeSelect
        label={t("common.entity.pipelineflowtransport.search.form.field.freighttype", "Freight Type")}
        placeholder={t<string>("common.entity.pipelineflowtransport.search.form.field.object.freighttype", "Freight Type")}
        selector={{
          regionId: options?.regionId,
          cargoSubtype: options?.category === transportCategory.oil ? cargoSubtype.oil : undefined
        }}
        clearable
        withinPortal={true}
        {...form.getInputProps("freightType")} />
      <ServiceTypeSelect
        label={t("common.entity.pipellineflowtransport.search.form.field.servicetype", "Service Type")}
        placeholder={t<string>("common.entity.pipelineflowtransport.search.form.field.object.servicetype", "Service Type")}
        selector={{
          regionId: options?.regionId,
        }}
        clearable
        withinPortal={true}
        {...form.getInputProps("serviceType")} />
      <Divider my="sm" variant="dotted"/>
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface PipelineFlowTransportSearchFormModalProps extends PipelineFlowTransportSearchFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const PipelineFlowTransportSearchFormModal = (props: PipelineFlowTransportSearchFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <PipelineFlowTransportSearchForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        submitting={submitting}/>
    </Modal>
  );
};
